import {
  BausteinAuswahlStatus,
  SpartenType,
  TaasTarifrisikoschluesselURI,
  TaasUriData,
} from '@ps20/bff-models';
import {
  convertProduktNameToTaasProduktName,
  convertUnfTaasBausteinToTarifrisikoschluessel,
  Leistung,
  LeistungEigenschaft,
} from '@ps20-frontend/frontend-models';
import {
  buildBasicSalesProductsForShop,
  buildBasicSalesProductsForStrecke,
  parseDatum,
} from '@ps20-frontend/preis-lib';
import { IAppState } from '../../appState';
import { Berufsrisikogruppe } from '../../model/unf.enums';
import { Vp } from '../../model/vp';

export function removeProfiSport(vp: Vp, unfData: TaasUriData): void {
  if (vp.berufsrisikogruppe === Berufsrisikogruppe.PROFISPORTLER) {
    for (const key in unfData.salesProducts) {
      if (Object.hasOwn(unfData.salesProducts, key)) {
        const akutleistung = unfData.salesProducts[key].find(
          option => option.schluessel === TaasTarifrisikoschluesselURI.AKUTLEISTUNG,
        );
        if (akutleistung) {
          akutleistung.ausgewaehlt = false;
        }
      }
    }
  }
}

export function buildTaasUnfDataForStrecke(state: IAppState): TaasUriData {
  const unfData: TaasUriData = generateBasicTaasUnfData(state);
  unfData.salesProducts = buildBasicSalesProductsForStrecke(
    state.produkt,
    convertUnfTaasBausteinToTarifrisikoschluessel,
  );
  removeProfiSport(state.vp, unfData);
  addZusatzoptionsBetrag(unfData, state);
  addLeistungen(unfData, state);
  return unfData;
}

export function buildTaasUnfDataForShop(state: IAppState): TaasUriData {
  const unfData: TaasUriData = generateBasicTaasUnfData(state);
  unfData.salesProducts = buildBasicSalesProductsForShop(
    state.produkt,
    convertUnfTaasBausteinToTarifrisikoschluessel,
  );
  addZusatzoptionsBetrag(unfData, state);
  addLeistungen(unfData, state);
  return unfData;
}

function generateBasicTaasUnfData(state: IAppState): TaasUriData {
  const vp = state.vp;

  return {
    spartenType: SpartenType.UNFALL,
    geburtsdatumVN: parseDatum(state.versicherungsnehmer.geburtsdatum),
    salesProducts: null,
    berufsrisikogruppe:
      vp.berufsrisikogruppe !== Berufsrisikogruppe.ARTIST_STUNTMAN_DOMPTEUR
        ? vp.berufsrisikogruppe
        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (null as any),
    ...addSpecialSportRisk(vp),
    vpGeburtsdatum: parseDatum(vp.geburtsdatum),
    vpAnrede: vp.anrede,
    vpPlz: vp.adresse.plz,
    vpLand: vp.adresse.land?.wert || 'DE',
    vnPlz: state.versicherungsnehmer.adresse?.plz,
    vnLand: state.versicherungsnehmer.adresse?.land?.wert || 'DE',
    urbeginndatum: state.vertragsoptionen?.urbeginn,
  };
}

function addSpecialSportRisk(vp: Vp): Partial<TaasUriData> {
  if (
    vp.berufsrisikogruppe !== Berufsrisikogruppe.PROFISPORTLER ||
    (vp.profiSport?.sportId === null && vp.profiSport?.profiSportRisk === null)
  ) {
    return {};
  }

  return {
    profiId: vp.profiSport?.sportId,
    profiSportRisk: vp.profiSport?.profiSportRisk,
    profiSportName: vp.profiSport?.sportName,
  };
}

function addZusatzoptionsBetrag(unfData: TaasUriData, state: IAppState): void {
  Object.keys(unfData.salesProducts).forEach(key => {
    const produktline = state.produkt.produktlinien.find(
      linie => convertProduktNameToTaasProduktName(linie.produktName) === key,
    );
    unfData.salesProducts[key] = unfData.salesProducts[key].map(zusatzoption => {
      const zusatzoptionImState = produktline.zusatzoptionen.find(
        option => option.name === zusatzoption.schluessel,
      );
      const eigenschaftIsNumber = !Number.isNaN(Number(zusatzoptionImState.eigenschaft));

      let newZusatzoption;
      if (eigenschaftIsNumber) {
        newZusatzoption = {
          ...zusatzoption,
          schluessel: zusatzoptionImState.name as TaasTarifrisikoschluesselURI,
          betrag: Number(zusatzoptionImState.eigenschaft),
        };
      } else {
        newZusatzoption = {
          ...zusatzoption,
          schluessel: zusatzoptionImState.name as TaasTarifrisikoschluesselURI,
        };
      }

      return newZusatzoption;
    });
  });
}

function addLeistungen(unfData: TaasUriData, state: IAppState): void {
  Object.keys(unfData.salesProducts).forEach(key => {
    const produktline = state.produkt.produktlinien.find(
      linie => convertProduktNameToTaasProduktName(linie.produktName) === key,
    );
    const taasLeistungen = produktline.leistungen.map(buildLeistung).filter(Boolean);
    unfData.salesProducts[key].push(...taasLeistungen);
  });
}

function buildLeistung(
  leistung: Leistung,
): BausteinAuswahlStatus<TaasTarifrisikoschluesselURI> | null {
  if (leistung.eigenschaft === null) {
    return null;
  }

  const eigenschaftIsNumber = !Number.isNaN(Number(leistung.eigenschaft));
  let isAusgewaehlt = true;
  if (!eigenschaftIsNumber) {
    isAusgewaehlt = leistung.eigenschaft === LeistungEigenschaft.INKLUSIVE;
  }

  const taasLeistung: BausteinAuswahlStatus<TaasTarifrisikoschluesselURI> = {
    schluessel: leistung.name as TaasTarifrisikoschluesselURI,
    ausgewaehlt: isAusgewaehlt,
    isGefahr: false,
    isInklusiv: isAusgewaehlt,
  };
  if (eigenschaftIsNumber) {
    taasLeistung.betrag = Number(leistung.eigenschaft);
  }

  return taasLeistung;
}
